import React, {useCallback, useMemo} from 'react'
import {Box, Flex, Radio, RadioGroup} from '@chakra-ui/react'
import PropTypes from 'prop-types'

/**
 * Renders a single radio item for a radio filter.
 * @param {any} [filterId]
 * @param {{value: any, label: string}} option
 * @param {function} [_onChange]
 * @returns {JSX.Element}
 * @constructor
 */
const RadioFilterItem = ({filterId, option, onChange: _onChange}) => {
    const onChange = useCallback(() => {
        _onChange?.(option, {
            filterId,
            select: true,
            multiple: false
        })
    }, [filterId, option, _onChange])

    return (
        <Box>
            <Radio
                size="md"
                display="flex"
                alignItems="center"
                // use padding to provide enough touchable area on mobile
                paddingY={2}
                // negative margin to compensate for the padding
                marginY={-2}
                value={option.value}
                onChange={onChange}
            >
                {option.label}
            </Radio>
        </Box>
    )
}

RadioFilterItem.propTypes = {
    filterId: PropTypes.any,
    option: PropTypes.object.isRequired,
    onChange: PropTypes.func
}

/**
 * Renders a radio filter. Based on the original RadioRefinements component.
 * @param {any} [filterId]
 * @param {array} [options]
 * @param {any} [selectedOption]
 * @param {function} [onChange]
 * @returns {JSX.Element|null}
 * @constructor
 */
const RadioFilter = ({filterId, options, selectedOption, onChange}) => {
    const displayedOptions = useMemo(
        () => options?.filter((option) => option.hitCount == null || option.hitCount > 0),
        [options]
    )

    if (!displayedOptions?.length) {
        return null
    }

    return (
        <Box>
            <RadioGroup
                // The following `false` fallback is required to avoid the radio group
                // from switching to "uncontrolled mode" when `selected` is empty.
                value={selectedOption ?? false}
            >
                <Flex flexDirection="column" gap={4}>
                    {displayedOptions.map((option) => (
                        <RadioFilterItem
                            key={option.value}
                            filterId={filterId}
                            option={option}
                            onChange={onChange}
                        />
                    ))}
                </Flex>
            </RadioGroup>
        </Box>
    )
}

RadioFilter.propTypes = {
    filterId: PropTypes.any,
    options: PropTypes.array,
    selectedOption: PropTypes.any,
    onChange: PropTypes.func
}

export default RadioFilter
